import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PillButton } from '../components/ui/PillButton';
import { TextInput } from '../components/ui/TextInput';
import { api, ApiError } from '../lib/api/client';
import { useAuth } from '../lib/auth/store';

export const Route = createFileRoute('/_auth/signup')({
  component: SignupPage,
});

const schema = z.object({
  inviteCode: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required').max(64),
  email: z.string().email(),
  password: z.string().min(8, 'Min 8 characters'),
});

function SignupPage() {
  const router = useRouter();
  const setSession = useAuth((s) => s.setSession);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ inviteCode: string; name: string; email: string; password: string }>({
    resolver: zodResolver(schema),
    defaultValues: { inviteCode: '', name: '', email: '', password: '' },
  });

  async function onSubmit(values: { inviteCode: string; name: string; email: string; password: string }) {
    try {
      const res = await api.auth.signup(values);
      setSession(res.user, { accessToken: res.accessToken, refreshToken: res.refreshToken });
      router.navigate({ to: '/dashboard' });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Sign up failed';
      const code = err instanceof ApiError ? err.code : undefined;
      if (code === 'INVALID_INVITE') setError('inviteCode', { message });
      else if (code === 'EMAIL_TAKEN') setError('email', { message });
      else setError('root', { message });
    }
  }

  return (
    <div>
      <p className="mono-label text-muted-slate">REQUEST ACCESS</p>
      <h1 className="font-display text-card-heading text-ink mt-3">Create your account.</h1>
      <p className="text-body text-muted-slate mt-2">
        mindrift is invite-only. Enter the code shared with you.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 flex flex-col gap-5">
        <TextInput
          label="Invite code"
          autoFocus
          autoComplete="off"
          {...register('inviteCode')}
          error={errors.inviteCode?.message}
        />
        <TextInput
          label="Name"
          autoComplete="name"
          {...register('name')}
          error={errors.name?.message}
        />
        <TextInput
          label="Email"
          type="email"
          autoComplete="email"
          {...register('email')}
          error={errors.email?.message}
        />
        <TextInput
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 8 characters"
          {...register('password')}
          error={errors.password?.message}
        />
        {errors.root?.message && (
          <p className="text-caption text-error-red">{errors.root.message}</p>
        )}
        <PillButton type="submit" loading={isSubmitting}>Create account</PillButton>
      </form>

      <p className="mt-8 text-caption text-muted-slate">
        Already have access?{' '}
        <Link to="/login" className="text-link">Sign in</Link>
      </p>
    </div>
  );
}
